import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  EmptyState,
  LoadingState,
  PageHeader,
  PageLayout,
  SectionCard,
} from '../../components/ui';
import {
  createJobOffer,
  fetchJobOffer,
  previewCalculation,
  updateJobOffer,
} from '../../services/jobOffersApi';

const NUMBER_FIELDS = [
  'proposed_total_package',
  'budgeted_total_package',
  'monthly_billing_rate',
  'billing_rate_pct',
  'basic_pct',
  'housing_pct_of_basic',
  'transport_pct_of_basic',
  'ticket_allowance',
  'makeup_subsidy',
  'other_allowance',
  'iqama_yearly_cost',
  'exit_reentry',
  'gosi_pct',
  'health_yearly_cost_per_person',
  'air_ticket_business_cost',
  'agency_fees',
  'saudization',
];

const INTEGER_FIELDS = [
  'offer_validity_days',
  'annual_leave_days',
  'probation_days',
  'health_persons',
  'air_tickets_count',
];

const DEFAULT_FORM = {
  candidate_name: '',
  candidate_name_ar: '',
  nationality: '',
  nationality_ar: '',
  id_passport_no: '',
  contact_no: '+966',
  email: '',
  position: '',
  position_ar: '',
  project_name: '',
  project_name_ar: '',
  project_post: '',
  project_post_ar: '',
  location: 'Riyadh or any other place where the company conducts business.',
  location_ar: 'الرياض أو أي جهة أخرى تمارس فيها الشركة نشاطها.',
  contract_status: 'family',
  contract_type: 'fixed',
  offer_date: new Date().toISOString().slice(0, 10),
  offer_validity_days: 5,
  expected_joining_date: '',
  annual_leave_days: 30,
  probation_days: 180,
  billing_rate_pct: 0.20,
  monthly_billing_rate: '',
  budgeted_total_package: '',
  proposed_total_package: '',
  conditional_terms: '',
  conditional_terms_ar: '',
  notes: '',

  basic_pct: 0.69,
  housing_pct_of_basic: 0.25,
  transport_pct_of_basic: 0.10,
  ticket_allowance: 200,
  ticket_allowance_type: 'Saudi/Expat',
  makeup_subsidy: 0,
  other_allowance: 0,

  iqama_yearly_cost: 10350,
  iqama_type: 'Saudi/Expat',
  exit_reentry: 0,
  gosi_pct: 0.02,
  gosi_type: 'Saudi/Expat',
  health_yearly_cost_per_person: 3000,
  health_persons: 1,
  health_type: 'Family/Single',
  air_ticket_business_cost: 0,
  air_tickets_count: 0,

  agency_fees: 1000,
  agency_fees_type: 'Saudi/Expat',
  saudization: 0,
};

function toNumber(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtPct(value, decimals = 1) {
  if (value === null || value === undefined || value === '') return '—';
  return `${(Number(value) * 100).toFixed(decimals)}%`;
}

export default function JobOfferForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editing = Boolean(id);

  // When opened from an approved pre-approval ("Create Job Offer"), the mapped
  // fields arrive via router state and pre-fill the new offer. Create mode only;
  // empty values are filtered out by the caller so form defaults stay intact.
  const prefill =
    !editing && location.state?.jobOfferPrefill && typeof location.state.jobOfferPrefill === 'object'
      ? location.state.jobOfferPrefill
      : null;
  const prefillSourceRef = prefill ? location.state?.fromPreApprovalRef || null : null;

  const [form, setForm] = useState(() => (prefill ? { ...DEFAULT_FORM, ...prefill } : DEFAULT_FORM));
  const [originalStatus, setOriginalStatus] = useState('draft');
  const [loading, setLoading] = useState(editing);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [calc, setCalc] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);

  const setField = (key) => (event) => {
    const value = event?.target?.value ?? event;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setNumeric = (key) => (event) => {
    const value = event?.target?.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (!editing) return;
    let mounted = true;
    setLoading(true);
    setLoadError(null);

    fetchJobOffer(id)
      .then((offer) => {
        if (!mounted) return;
        setOriginalStatus(offer.status);
        const c = offer.calculation || {};
        setForm((prev) => ({
          ...prev,
          ...prev,
          candidate_name: offer.candidate_name ?? '',
          candidate_name_ar: offer.candidate_name_ar ?? '',
          nationality: offer.nationality ?? '',
          nationality_ar: offer.nationality_ar ?? '',
          id_passport_no: offer.id_passport_no ?? '',
          contact_no: offer.contact_no ?? '+966',
          email: offer.email ?? '',
          position: offer.position ?? '',
          position_ar: offer.position_ar ?? '',
          project_name: offer.project_name ?? '',
          project_name_ar: offer.project_name_ar ?? '',
          project_post: offer.project_post ?? '',
          project_post_ar: offer.project_post_ar ?? '',
          location: offer.location ?? prev.location,
          location_ar: offer.location_ar ?? prev.location_ar,
          contract_status: offer.contract_status ?? 'family',
          contract_type: offer.contract_type ?? 'fixed',
          offer_date: offer.offer_date ? offer.offer_date.slice(0, 10) : prev.offer_date,
          offer_validity_days: offer.offer_validity_days ?? 5,
          expected_joining_date: offer.expected_joining_date ? offer.expected_joining_date.slice(0, 10) : '',
          annual_leave_days: offer.annual_leave_days ?? 30,
          probation_days: offer.probation_days ?? 180,
          billing_rate_pct: Number(offer.billing_rate_pct ?? 0.20),
          monthly_billing_rate: offer.monthly_billing_rate ?? '',
          budgeted_total_package: offer.budgeted_total_package ?? '',
          proposed_total_package: offer.proposed_total_package ?? '',
          conditional_terms: offer.conditional_terms ?? '',
          conditional_terms_ar: offer.conditional_terms_ar ?? '',
          notes: offer.notes ?? '',
          basic_pct: Number(c.basic_pct ?? 0.69),
          housing_pct_of_basic: Number(c.housing_pct_of_basic ?? 0.25),
          transport_pct_of_basic: Number(c.transport_pct_of_basic ?? 0.10),
          ticket_allowance: Number(c.ticket_allowance ?? 200),
          ticket_allowance_type: c.ticket_allowance_type ?? 'Saudi/Expat',
          makeup_subsidy: Number(c.makeup_subsidy ?? 0),
          other_allowance: Number(c.other_allowance ?? 0),
          iqama_yearly_cost: Number(c.iqama_yearly_cost ?? 10350),
          iqama_type: c.iqama_type ?? 'Saudi/Expat',
          exit_reentry: Number(c.exit_reentry ?? 0),
          gosi_pct: Number(c.gosi_pct ?? 0.02),
          gosi_type: c.gosi_type ?? 'Saudi/Expat',
          health_yearly_cost_per_person: Number(c.health_yearly_cost_per_person ?? 3000),
          health_persons: Number(c.health_persons ?? 1),
          health_type: c.health_type ?? 'Family/Single',
          air_ticket_business_cost: Number(c.air_ticket_business_cost ?? 0),
          air_tickets_count: Number(c.air_tickets_count ?? 0),
          agency_fees: Number(c.agency_fees ?? 1000),
          agency_fees_type: c.agency_fees_type ?? 'Saudi/Expat',
          saudization: Number(c.saudization ?? 0),
        }));
      })
      .catch((err) => {
        console.error('Failed to load job offer', err);
        setLoadError(err?.response?.data?.message || 'Failed to load job offer.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [editing, id]);

  const recalculate = useCallback(async () => {
    if (!toNumber(form.proposed_total_package)) {
      setCalc(null);
      return;
    }
    setCalcLoading(true);
    try {
      const payload = buildCalcPayload(form);
      const data = await previewCalculation(payload);
      setCalc(data);
    } catch (err) {
      console.error('Failed to preview calculation', err);
    } finally {
      setCalcLoading(false);
    }
  }, [form]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      recalculate();
    }, 250);
    return () => window.clearTimeout(id);
  }, [recalculate]);

  const handleSubmit = async (action) => {
    if (!form.candidate_name?.trim()) {
      toast.error('Candidate name is required.');
      return;
    }
    if (!form.position?.trim()) {
      toast.error('Position is required.');
      return;
    }
    if (!toNumber(form.proposed_total_package)) {
      toast.error('Proposed total package is required.');
      return;
    }

    setSaving(true);
    try {
      const payload = buildSavePayload(form, action, editing, originalStatus);
      const offer = editing
        ? await updateJobOffer(id, payload)
        : await createJobOffer(payload);

      toast.success(editing ? 'Job offer updated.' : 'Job offer created.');
      navigate(`/hr/job-offers/${offer.id}`);
    } catch (err) {
      console.error('Failed to save job offer', err);
      const validation = err?.response?.data?.errors;
      const message = err?.response?.data?.message || 'Failed to save job offer.';
      toast.error(validation ? Object.values(validation).flat().join(' ') : message);
    } finally {
      setSaving(false);
    }
  };

  const submitLabel = useMemo(() => {
    if (originalStatus === 'revision_requested') return 'Resubmit for Approval';
    return 'Submit for Approval';
  }, [originalStatus]);

  const submitAction = originalStatus === 'revision_requested' ? 'resubmit' : 'submit';

  if (loading) {
    return (
      <PageLayout>
        <LoadingState rows={10} header />
      </PageLayout>
    );
  }

  if (loadError) {
    return (
      <PageLayout>
        <EmptyState
          title="Could not load offer"
          description={loadError}
          actionLabel="Back to list"
          onAction={() => navigate('/hr/job-offers')}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout backgroundAccent>
      <PageHeader
        overline={editing ? `Editing ${form.candidate_name || 'offer'}` : 'New Job Offer'}
        title={editing ? 'Edit Job Offer' : 'Create Job Offer'}
        subtitle="HR team drafts the offer; HR admin approves, requests revision, or rejects."
        actions={(
          <Stack direction="row" spacing={1}>
            <Button
              variant="text"
              color="inherit"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate('/hr/job-offers')}
            >
              Back
            </Button>
          </Stack>
        )}
      />

      {originalStatus === 'revision_requested' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          HR admin requested a revision. Adjust the offer and use <strong>Resubmit for Approval</strong>.
        </Alert>
      )}

      {prefillSourceRef && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Pre-filled from approved pre-approval <strong>{prefillSourceRef}</strong>. Review the values and complete the remaining required fields.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <SectionCard title="Candidate">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Candidate Name (English)"
                    value={form.candidate_name}
                    onChange={setField('candidate_name')}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Candidate Name (Arabic)"
                    value={form.candidate_name_ar}
                    onChange={setField('candidate_name_ar')}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label="Nationality (English)" value={form.nationality} onChange={setField('nationality')} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="الجنسية (Arabic)"
                    value={form.nationality_ar}
                    onChange={setField('nationality_ar')}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField fullWidth label="ID / Passport No" value={form.id_passport_no} onChange={setField('id_passport_no')} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField fullWidth label="Contact No" value={form.contact_no} onChange={setField('contact_no')} />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField fullWidth label="Email" value={form.email} onChange={setField('email')} />
                </Grid>
              </Grid>
            
          </SectionCard>

          <SectionCard title="Position & Contract" sx={{ mt: 2 }}>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Position (English)"
                    value={form.position}
                    onChange={setField('position')}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="المُسمّى الوظيفي (Arabic)"
                    value={form.position_ar}
                    onChange={setField('position_ar')}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label="Project / Client (English)" value={form.project_name} onChange={setField('project_name')} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="المشروع / العميل (Arabic)"
                    value={form.project_name_ar}
                    onChange={setField('project_name_ar')}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label="Project Post / City (English)" value={form.project_post} onChange={setField('project_post')} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="موقع المشروع (Arabic)"
                    value={form.project_post_ar}
                    onChange={setField('project_post_ar')}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label="Location (English)" value={form.location} onChange={setField('location')} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="مكان العمل (Arabic)"
                    value={form.location_ar}
                    onChange={setField('location_ar')}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField select fullWidth label="Contract Status" value={form.contract_status} onChange={setField('contract_status')}>
                    <MenuItem value="family">Family</MenuItem>
                    <MenuItem value="single">Single</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField select fullWidth label="Contract Type" value={form.contract_type} onChange={setField('contract_type')}>
                    <MenuItem value="fixed">Fixed Term</MenuItem>
                    <MenuItem value="unlimited">Unlimited</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField fullWidth type="date" label="Offer Date" InputLabelProps={{ shrink: true }} value={form.offer_date} onChange={setField('offer_date')} />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField fullWidth type="number" label="Validity (days)" value={form.offer_validity_days} onChange={setNumeric('offer_validity_days')} />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField fullWidth type="date" label="Expected Joining Date" InputLabelProps={{ shrink: true }} value={form.expected_joining_date} onChange={setField('expected_joining_date')} />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField fullWidth type="number" label="Annual Leave (days)" value={form.annual_leave_days} onChange={setNumeric('annual_leave_days')} />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField fullWidth type="number" label="Probation (days)" value={form.probation_days} onChange={setNumeric('probation_days')} />
                </Grid>
              </Grid>
            
          </SectionCard>

          <SectionCard title="Salary Inputs" sx={{ mt: 2 }}>
            
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Proposed Monthly Total Package"
                    value={form.proposed_total_package}
                    onChange={setNumeric('proposed_total_package')}
                    InputProps={{ endAdornment: <InputAdornment position="end">SR</InputAdornment> }}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Budgeted Monthly Package"
                    value={form.budgeted_total_package}
                    onChange={setNumeric('budgeted_total_package')}
                    InputProps={{ endAdornment: <InputAdornment position="end">SR</InputAdornment> }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Billing Rate %"
                    value={form.billing_rate_pct}
                    onChange={setNumeric('billing_rate_pct')}
                    helperText="0.20 = 20% margin"
                    inputProps={{ step: 0.01, min: 0, max: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Monthly Billing Rate (override)"
                    value={form.monthly_billing_rate}
                    onChange={setNumeric('monthly_billing_rate')}
                    helperText="Leave blank to derive from billing rate %"
                    InputProps={{ endAdornment: <InputAdornment position="end">SR</InputAdornment> }}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Basic % of Package" value={form.basic_pct} onChange={setNumeric('basic_pct')} inputProps={{ step: 0.01, min: 0, max: 1 }} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Housing % of (Basic + Area)" value={form.housing_pct_of_basic} onChange={setNumeric('housing_pct_of_basic')} inputProps={{ step: 0.01, min: 0, max: 1 }} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Transport % of (Basic + Area)" value={form.transport_pct_of_basic} onChange={setNumeric('transport_pct_of_basic')} inputProps={{ step: 0.01, min: 0, max: 1 }} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Ticket Allowance" value={form.ticket_allowance} onChange={setNumeric('ticket_allowance')} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Make-Up Subsidy" value={form.makeup_subsidy} onChange={setNumeric('makeup_subsidy')} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Other Allowance" value={form.other_allowance} onChange={setNumeric('other_allowance')} />
                </Grid>
              </Grid>
            
          </SectionCard>

          <SectionCard title="Overhead Costs" sx={{ mt: 2 }}>
            
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Iqama Yearly Cost" value={form.iqama_yearly_cost} onChange={setNumeric('iqama_yearly_cost')} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Exit / Re-entry" value={form.exit_reentry} onChange={setNumeric('exit_reentry')} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="GOSI %" value={form.gosi_pct} onChange={setNumeric('gosi_pct')} inputProps={{ step: 0.01, min: 0, max: 1 }} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Health Cost / Person / Year" value={form.health_yearly_cost_per_person} onChange={setNumeric('health_yearly_cost_per_person')} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Health Persons" value={form.health_persons} onChange={setNumeric('health_persons')} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField select fullWidth label="Health Type" value={form.health_type} onChange={setField('health_type')}>
                    <MenuItem value="Family/Single">Family / Single</MenuItem>
                    <MenuItem value="Single">Single</MenuItem>
                    <MenuItem value="Family">Family</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Air Ticket — Business cost / yr" value={form.air_ticket_business_cost} onChange={setNumeric('air_ticket_business_cost')} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Air Tickets Count" value={form.air_tickets_count} onChange={setNumeric('air_tickets_count')} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Agency Fees" value={form.agency_fees} onChange={setNumeric('agency_fees')} />
                </Grid>
                <Grid size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth type="number" label="Saudization" value={form.saudization} onChange={setNumeric('saudization')} />
                </Grid>
              </Grid>
            
          </SectionCard>

          <SectionCard title="Additional Notes" sx={{ mt: 2 }}>

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Conditional Terms (English)"
                  value={form.conditional_terms}
                  onChange={setField('conditional_terms')}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="الشروط الإضافية (Arabic)"
                  value={form.conditional_terms_ar}
                  onChange={setField('conditional_terms_ar')}
                  inputProps={{ dir: 'rtl' }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Internal Notes"
                  value={form.notes}
                  onChange={setField('notes')}
                />
              </Stack>

          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <CalculationSummary calc={calc} loading={calcLoading} />

          <Stack spacing={1.5} sx={{ mt: 2 }}>
            <Button
              size="large"
              variant="outlined"
              startIcon={<SaveRoundedIcon />}
              disabled={saving}
              onClick={() => handleSubmit('save')}
            >
              {saving ? 'Saving…' : 'Save as Draft'}
            </Button>
            <Button
              size="large"
              variant="contained"
              startIcon={<SendRoundedIcon />}
              disabled={saving}
              onClick={() => handleSubmit(submitAction)}
            >
              {saving ? 'Submitting…' : submitLabel}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </PageLayout>
  );
}

function CalculationSummary({ calc, loading }) {
  if (!calc) {
    return (
      <SectionCard title="Calculation Preview">
        <Typography variant="body2" color="text.secondary">
          Enter the proposed total package to preview the breakdown.
        </Typography>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Calculation Preview" subheader={loading ? 'Updating…' : 'Live preview'}>
      <Stack spacing={1}>
        <Row label="Basic + Area Allowance" value={fmtMoney(calc.basic_plus_area)} />
        <Row label="Housing" value={fmtMoney(calc.housing)} />
        <Row label="Transport" value={fmtMoney(calc.transport)} />
        <Row label="Living Allowance" value={fmtMoney(calc.living_allowance)} />
        <Row label="Ticket Allowance" value={fmtMoney(calc.ticket_allowance)} />
        <Divider />
        <Row label="Subtotal Basic" value={fmtMoney(calc.subtotal_basic)} bold />
        <Divider />
        <Row label="Indemnity / month" value={fmtMoney(calc.indemnity)} />
        <Row label="Leave Cost / month" value={fmtMoney(calc.leave_cost)} />
        <Row label="Iqama / month" value={fmtMoney(calc.iqama_cost)} />
        <Row label="GOSI" value={fmtMoney(calc.gosi)} />
        <Row label="Health Insurance" value={fmtMoney(calc.health_insurance)} />
        <Row label="Air Tickets" value={fmtMoney(calc.air_tickets)} />
        <Row label="Subtotal Required Overhead" value={fmtMoney(calc.subtotal_required_overhead)} bold />
        <Row label="Agency Fees" value={fmtMoney(calc.agency_fees)} />
        <Row label="Saudization" value={fmtMoney(calc.saudization)} />
        <Row label="Subtotal Optional Overhead" value={fmtMoney(calc.subtotal_optional_overhead)} bold />
        <Divider />
        <Row label="Total Monthly Package" value={fmtMoney(calc.total_monthly_package)} highlight />
        <Row label="Billing Price" value={fmtMoney(calc.billing_price)} />
        <Row label="Margin Profit" value={fmtMoney(calc.margin_profit)} />
        <Row label="Profitability" value={fmtPct(calc.profitability_pct)} />
        <Divider />
        <Row label="Indemnity / year" value={fmtMoney(calc.indemnity_per_year)} />
      </Stack>
    </SectionCard>
  );
}

function Row({ label, value, bold = false, highlight = false }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        ...(highlight ? { backgroundColor: 'success.light', color: 'success.contrastText', px: 1, py: 0.5, borderRadius: 1 } : null),
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 500 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: bold || highlight ? 700 : 500 }}>{value}</Typography>
    </Box>
  );
}

function buildCalcPayload(form) {
  const payload = { proposed_total_package: toNumber(form.proposed_total_package) || 0 };
  NUMBER_FIELDS.forEach((field) => {
    const value = toNumber(form[field]);
    if (value !== null) payload[field] = value;
  });
  INTEGER_FIELDS.forEach((field) => {
    const value = toNumber(form[field]);
    if (value !== null) payload[field] = Math.round(value);
  });
  ['ticket_allowance_type', 'iqama_type', 'gosi_type', 'health_type', 'agency_fees_type'].forEach((field) => {
    if (form[field]) payload[field] = form[field];
  });
  return payload;
}

function buildSavePayload(form, action, editing, originalStatus) {
  const payload = {
    candidate_id: form.candidate_id ?? null,
    candidate_name: form.candidate_name?.trim(),
    candidate_name_ar: form.candidate_name_ar?.trim() || null,
    nationality: form.nationality?.trim() || null,
    nationality_ar: form.nationality_ar?.trim() || null,
    id_passport_no: form.id_passport_no?.trim() || null,
    contact_no: form.contact_no?.trim() || null,
    email: form.email?.trim() || null,
    position: form.position?.trim(),
    position_ar: form.position_ar?.trim() || null,
    project_name: form.project_name?.trim() || null,
    project_name_ar: form.project_name_ar?.trim() || null,
    project_post: form.project_post?.trim() || null,
    project_post_ar: form.project_post_ar?.trim() || null,
    location: form.location?.trim() || null,
    location_ar: form.location_ar?.trim() || null,
    contract_status: form.contract_status,
    contract_type: form.contract_type,
    offer_date: form.offer_date || null,
    offer_validity_days: toNumber(form.offer_validity_days) || 5,
    expected_joining_date: form.expected_joining_date || null,
    annual_leave_days: toNumber(form.annual_leave_days) || 30,
    probation_days: toNumber(form.probation_days) || 180,
    billing_rate_pct: toNumber(form.billing_rate_pct) ?? 0.2,
    monthly_billing_rate: toNumber(form.monthly_billing_rate),
    budgeted_total_package: toNumber(form.budgeted_total_package),
    proposed_total_package: toNumber(form.proposed_total_package) || 0,
    conditional_terms: form.conditional_terms?.trim() || null,
    conditional_terms_ar: form.conditional_terms_ar?.trim() || null,
    notes: form.notes?.trim() || null,
    action,
    ...buildCalcPayload(form),
  };

  if (editing && originalStatus === 'revision_requested' && action === 'submit') {
    payload.action = 'resubmit';
  }
  return payload;
}
